import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface RevealProps {
    children: ReactNode
    className?: string
    /** Stagger offset in milliseconds. */
    delay?: number
}

/* Fades + lifts its children into view the first time they enter the
   viewport. Mirrors the eased, deliberate motion of the hero entrance. */
export const Reveal: FC<RevealProps> = ({
    children,
    className,
    delay = 0
}): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true)
                        observer.unobserve(entry.target)
                    }
                })
            },
            { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return (
        <div
            ref={ref}
            className={cn('reveal', visible && 'is-visible', className)}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    )
}
